import React, { cloneElement, isValidElement } from 'react';

import { union } from 'lodash';

import { MENU_PATHS } from 'containers/App/TimeTracker/constants';
import { PATHS as ABSENCES_PATHS } from 'packages/absences/constants';
import { PATHS as ADDRESSES_PATHS } from 'packages/addresses/constants';
import { PATHS as CHECKPOINT_ROUTES_PATHS } from 'packages/checkpointRoutes/constants';
import { PATHS as DASHBOARD_PATHS } from 'packages/dashboard/constants';
import { PATHS as DOCUMENT_REQUESTS_PATHS } from 'packages/documentRequests/constants';
import { PATHS as EMERGENCY_PATHS } from 'packages/emergencies/constants';
import { PATHS as EVENT_TYPES_PATHS } from 'packages/eventTypes/constants';
import { PATHS as EVENTS_PATHS } from 'packages/events/constants';
import { PATHS as GROUPS_PATHS } from 'packages/groups/constants';
import { PATHS as HOLIDAYS_PATHS } from 'packages/holidays/constants';
import { PATHS as JOB_PATHS } from 'packages/jobs/constants';
import { PATHS as OPTION_LIST_PATHS } from 'packages/optionLists/constants';
import { PATHS as COST_CENTERS_PATHS } from 'packages/costCenters/constants';
import { PATHS as LOCATIONS_PATHS } from 'packages/locations/constants';

import { PATHS as MONITORING_PATHS } from 'packages/monitoring/constants';
import { PATHS as OPERATIONAL_PANEL_PATHS } from 'packages/operationalPanel/constants';
import { PATHS as OPERONDA_API_PATHS } from 'packages/operondaApi/constants';
import { PATHS as USER_POSITION_HISTORY_PATHS } from 'packages/reports/constants';
import { PATHS as SCHEDULES_PATHS } from 'packages/schedules/constants';
import { PATHS as SHIFTS_PATHS } from 'packages/shifts/constants';
import { PATHS as TRIGGERS_PATHS } from 'packages/triggers/constants';
import { getRouteToUsersHome } from 'packages/users/constants';
import { ALLOWED_ROLES, hasAnyAllowedRole } from 'packages/utils';
import { PATHS as WORK_JOURNEY_PATHS } from 'packages/workJourney/constants';
import { PATHS as WORK_ORDERS_PATHS } from 'packages/workOrders/constants';
import { PATHS as WORK_TEMPLATES_PATHS } from 'packages/workTemplates/constants';
import { PATHS as WORKER_SCORE_PATHS } from 'packages/workerScore/constants';
import { PATHS as CONFIG_PATHS } from 'routes/config/constants';

import MenuIcon from './components/MenuIcon';
import MenuLabel from './components/MenuLabel';
import {
  SIDEBAR_MENU_KEYS,
  SIDEBAR_SECTION_LABEL_IDS,
  TIME_TRACKER_GATED_KEYS,
} from './constants';
import styles from './styles.module.less';
import { compactMenuItems } from './utils/sidebarNav';

export const buildSidebarMenuItems = ({
  url,
  userOrgAccess,
  userOrgDivisions,
  timeTrackerUser,
  isSupervisor,
  supervisorThirdParty,
  isAdmin,
  locationsAlertsOrgsAllowed,
  emergenciesAllowed,
  workerScoreAllowed,
  costCentersAllowed,
}) => {
  const getItem = ({
    label,
    key,
    icon,
    children,
    type,
    allowedRoles,
    accessByRemoteConfig = true,
    disabled,
    hidden,
    className,
  }) => {
    const hasAccess = hasAnyAllowedRole(allowedRoles, userOrgAccess, userOrgDivisions);
    if (!hasAccess || !accessByRemoteConfig || hidden) {
      return null;
    }
    const compactChildren = children ? compactMenuItems(children) : undefined;
    if (children && compactChildren.length === 0) {
      return null;
    }
    const itemLabel =
      compactChildren && compactChildren.length > 0 && isValidElement(label)
        ? cloneElement(label, { count: compactChildren.length })
        : label;
    const labelProps = isValidElement(label) ? label.props : {};
    return {
      key,
      icon,
      to: labelProps.to,
      target: labelProps.target,
      labelId: labelProps.idLabelMessage,
      children: compactChildren,
      label: itemLabel,
      type,
      disabled,
      className,
    };
  };

  const createNavSection = (key, children) => {
    const compactChildren = compactMenuItems(children);
    if (compactChildren.length === 0) return null;
    return {
      key,
      label: <MenuLabel idLabelMessage={SIDEBAR_SECTION_LABEL_IDS[key]} />,
      children: compactChildren,
      className: styles.navSection,
    };
  };

  const isTimeTrackerGated = item =>
    TIME_TRACKER_GATED_KEYS.includes(item?.key) && !timeTrackerUser;

  const timeTrackerReportItems = [
    getItem({
      label: (
        <MenuLabel
          idLabelMessage="work.direction.attendance.report"
          to={`${url}/${MENU_PATHS.ATTENDANCE_REPORT}`}
        />
      ),
      key: SIDEBAR_MENU_KEYS.ATTENDANCE_REPORT,
    }),
    getItem({
      label: (
        <MenuLabel idLabelMessage="work.direction.daily.report" to={`${url}/${MENU_PATHS.DAILY}`} />
      ),
      key: SIDEBAR_MENU_KEYS.DAILY_REPORT,
    }),
    getItem({
      label: (
        <MenuLabel
          idLabelMessage="work.direction.holidays.report"
          to={`${url}/${MENU_PATHS.HOLIDAYS}`}
        />
      ),
      key: SIDEBAR_MENU_KEYS.HOLIDAYS_REPORT,
    }),
    getItem({
      label: (
        <MenuLabel
          idLabelMessage="work.direction.modifications.report"
          to={`${url}/${MENU_PATHS.MODIFICATIONS}`}
        />
      ),
      key: SIDEBAR_MENU_KEYS.MODIFICATIONS_REPORT,
    }),
  ];

  const timeTracker = getItem({
    label: <MenuLabel idLabelMessage="sidebar.menu.work.direction" />,
    key: SIDEBAR_MENU_KEYS.TIME_TRACKER,
    icon: <MenuIcon iconName="work_update" />,
    children: [
      ...timeTrackerReportItems,
      getItem({
        label: (
          <MenuLabel
            idLabelMessage="work.direction.issues.report"
            to={`${url}/${MENU_PATHS.ISSUES}`}
          />
        ),
        key: SIDEBAR_MENU_KEYS.ISSUES_REPORT,
      }),
      getItem({
        label: <MenuLabel idLabelMessage="work.direction.hash" to={`${url}/${MENU_PATHS.HASH}`} />,
        key: SIDEBAR_MENU_KEYS.HASH,
      }),
    ],
    hidden: !isAdmin && !isSupervisor,
  });

  const ownTimeTracker = getItem({
    label: <MenuLabel idLabelMessage="sidebar.menu.own.attendance.record" />,
    key: SIDEBAR_MENU_KEYS.OWN_TIME_TRACKER,
    icon: <MenuIcon iconName="work_update" />,
    children: [
      ...timeTrackerReportItems,
      getItem({
        label: <MenuLabel idLabelMessage="work.direction.hash" to={`${url}/${MENU_PATHS.HASH}`} />,
        key: SIDEBAR_MENU_KEYS.HASH,
      }),
    ],
    hidden: isAdmin || supervisorThirdParty,
  });

  const homeItem = getItem({
    allowedRoles: ALLOWED_ROLES.ORGANIZATIONS.HOME.LIST,
    label: <MenuLabel idLabelMessage="general.home" to={url} />,
    key: SIDEBAR_MENU_KEYS.HOME,
    icon: <MenuIcon iconName="home" />,
  });

  const realtimeControlItems = [
    getItem({
      allowedRoles: ALLOWED_ROLES.ORGANIZATIONS.OPERATIONAL_PANEL.OPERATION,
      label: (
        <MenuLabel
          idLabelMessage="operationalPanel.tab.operation"
          to={`${url}/${OPERATIONAL_PANEL_PATHS.BASE_URL}`}
        />
      ),
      key: SIDEBAR_MENU_KEYS.OPERATIONAL_PANEL,
      icon: <MenuIcon iconName="hexagon" />,
    }),
    getItem({
      label: (
        <MenuLabel
          idLabelMessage="sidebar.configuration.timeline"
          to={`${url}/${EVENTS_PATHS.BASE_URL}`}
        />
      ),
      key: SIDEBAR_MENU_KEYS.TIMELINE,
      allowedRoles: ALLOWED_ROLES.ORGANIZATIONS.EVENTS_SUMMARY.LIST,
      icon: <MenuIcon iconName="timeline" />,
    }),
    getItem({
      allowedRoles: ALLOWED_ROLES.ORGANIZATIONS.DIVISIONS.SCHEDULES.LIST,
      label: (
        <MenuLabel
          idLabelMessage="sidebar.dashboards.actives"
          to={`${url}/${DASHBOARD_PATHS.ACTIVE_SCHEDULES}`}
        />
      ),
      key: SIDEBAR_MENU_KEYS.ACTIVE_SCHEDULES,
      icon: <MenuIcon iconName="share" />,
    }),
    getItem({
      label: (
        <MenuLabel
          idLabelMessage="sidebar.menu.active.work.orders"
          to={`${url}/${WORK_ORDERS_PATHS.BASE_URL}`}
        />
      ),
      key: SIDEBAR_MENU_KEYS.WORK_ORDERS,
      allowedRoles: ALLOWED_ROLES.ORGANIZATIONS.DIVISIONS.WORK_ORDER.LIST,
      icon: <MenuIcon iconName="desktop_mac" />,
    }),
    getItem({
      allowedRoles: ALLOWED_ROLES.ORGANIZATIONS.DIVISIONS.SCHEDULES.LIST,
      label: (
        <MenuLabel
          idLabelMessage="sidebar.menu.checkpoint.routes.realtime"
          to={`${url}/${DASHBOARD_PATHS.ACTIVE_CHECKPOINT_ROUTES_DASHBOARD}`}
        />
      ),
      key: SIDEBAR_MENU_KEYS.CHECKPOINT_ROUTES_REALTIME,
      icon: <MenuIcon iconName="route" />,
    }),
    getItem({
      allowedRoles: ALLOWED_ROLES.ORGANIZATIONS.WORK_JOURNEY.LIST,
      label: (
        <MenuLabel
          idLabelMessage="workJourney.realTime.menu"
          to={`${url}/${WORK_JOURNEY_PATHS.REAL_TIME}`}
        />
      ),
      key: SIDEBAR_MENU_KEYS.WORK_JOURNEY_REAL_TIME,
      icon: <MenuIcon iconName="fact_check" />,
      accessByRemoteConfig: timeTrackerUser,
    }),
    getItem({
      allowedRoles: ALLOWED_ROLES.ORGANIZATIONS.EMERGENCIES.LIST,
      label: (
        <MenuLabel
          idLabelMessage="sidebar.menu.emergencyCommandCenter"
          to={`${url}/${EMERGENCY_PATHS.COMMAND_CENTER}`}
        />
      ),
      key: SIDEBAR_MENU_KEYS.EMERGENCY_COMMAND_CENTER,
      icon: <MenuIcon iconName="emergency" />,
      accessByRemoteConfig: emergenciesAllowed,
    }),
    getItem({
      allowedRoles: ALLOWED_ROLES.ORGANIZATIONS.EMERGENCIES.LIST,
      label: (
        <MenuLabel
          idLabelMessage="sidebar.menu.emergencyMonitoring"
          to={`${url}/${EMERGENCY_PATHS.MONITORING}`}
        />
      ),
      key: SIDEBAR_MENU_KEYS.EMERGENCY_MONITORING,
      icon: <MenuIcon iconName="health_and_safety" />,
      accessByRemoteConfig: emergenciesAllowed,
    }),
    getItem({
      allowedRoles: ALLOWED_ROLES.ORGANIZATIONS.DIVISIONS.DASHBOARDS.LIST,
      label: (
        <MenuLabel
          idLabelMessage="sidebar.dashboards.active_users"
          to={`${url}/${DASHBOARD_PATHS.ACTIVE_USER_DASHBOARD}`}
        />
      ),
      key: SIDEBAR_MENU_KEYS.ACTIVE_USERS,
      icon: <MenuIcon iconName="contacts" />,
    }),
    getItem({
      allowedRoles: ALLOWED_ROLES.ORGANIZATIONS.LOCATION_ALERTS.LIST,
      label: (
        <MenuLabel
          idLabelMessage="sidebar.menu.monitoring.location.alerts"
          to={`${url}/${MONITORING_PATHS.LOCATION_ALERTS}`}
        />
      ),
      key: SIDEBAR_MENU_KEYS.LOCATION_ALERTS,
      icon: <MenuIcon iconName="motion_sensor_urgent" />,
      accessByRemoteConfig: locationsAlertsOrgsAllowed,
    }),
  ];

  const operationsItems = [
    getItem({
      allowedRoles: ALLOWED_ROLES.ORGANIZATIONS.DIVISIONS.REPORTS.LIST,
      label: (
        <MenuLabel
          idLabelMessage="dashboard.operation.title"
          to={`${url}/${DASHBOARD_PATHS.OPERATION_REPORT}`}
        />
      ),
      key: SIDEBAR_MENU_KEYS.OPERATION_REPORT,
      icon: <MenuIcon iconName="add_chart" />,
    }),
    getItem({
      allowedRoles: ALLOWED_ROLES.ORGANIZATIONS.DIVISIONS.REPORTS.LIST,
      label: (
        <MenuLabel
          idLabelMessage="sidebar.dashboards.schedules"
          to={`${url}/${DASHBOARD_PATHS.SCHEDULES}`}
        />
      ),
      key: SIDEBAR_MENU_KEYS.SCHEDULE_REPORT,
      icon: <MenuIcon iconName="add_chart" />,
    }),
    getItem({
      allowedRoles: ALLOWED_ROLES.ORGANIZATIONS.DIVISIONS.REPORTS.LIST,
      label: (
        <MenuLabel
          idLabelMessage="sidebar.dashboards.tasks"
          to={`${url}/${DASHBOARD_PATHS.TASKS}`}
        />
      ),
      key: SIDEBAR_MENU_KEYS.TASK_REPORT,
      icon: <MenuIcon iconName="add_chart" />,
    }),
    getItem({
      allowedRoles: ALLOWED_ROLES.ORGANIZATIONS.DIVISIONS.REPORTS.LIST,
      label: (
        <MenuLabel
          idLabelMessage="sidebar.menu.checkpoint.routes.report"
          to={`${url}/${DASHBOARD_PATHS.CHECKPOINT_ROUTES}`}
        />
      ),
      key: SIDEBAR_MENU_KEYS.CHECKPOINT_ROUTES_REPORT,
      icon: <MenuIcon iconName="add_chart" />,
    }),
    getItem({
      allowedRoles: ALLOWED_ROLES.ORGANIZATIONS.USERS.LIST,
      label: (
        <MenuLabel
          idLabelMessage="sidebar.menu.workerScoreNotes"
          to={`${url}/${WORKER_SCORE_PATHS.NOTES}`}
        />
      ),
      key: SIDEBAR_MENU_KEYS.WORKER_SCORE_NOTES,
      icon: <MenuIcon iconName="star" />,
      accessByRemoteConfig: workerScoreAllowed,
    }),
    getItem({
      allowedRoles: ALLOWED_ROLES.ORGANIZATIONS.DASHBOARDS.CHARTS.LIST,
      label: (
        <MenuLabel
          idLabelMessage="sidebar.dashboards.metrics"
          to={`${url}/${DASHBOARD_PATHS.EVENTS}`}
        />
      ),
      key: SIDEBAR_MENU_KEYS.METRICS,
      icon: <MenuIcon iconName="show_chart" />,
    }),
    getItem({
      allowedRoles: ALLOWED_ROLES.ORGANIZATIONS.WORK_JOURNEY.UPDATE,
      label: (
        <MenuLabel
          idLabelMessage="workJourney.management.menu"
          to={`${url}/${WORK_JOURNEY_PATHS.WORK_JOURNEY_MANAGEMENT}`}
        />
      ),
      key: SIDEBAR_MENU_KEYS.WORK_JOURNEY_MANAGEMENT,
      icon: <MenuIcon iconName="event_available" />,
    }),
    getItem({
      allowedRoles: ALLOWED_ROLES.ORGANIZATIONS.ABSENCES.LIST,
      label: (
        <MenuLabel idLabelMessage="absences.title" to={`${url}/${ABSENCES_PATHS.BASE_URL}`} />
      ),
      key: SIDEBAR_MENU_KEYS.ABSENCES,
      icon: <MenuIcon iconName="event_busy" />,
      accessByRemoteConfig: timeTrackerUser,
    }),
    getItem({
      allowedRoles: ALLOWED_ROLES.ORGANIZATIONS.DIVISIONS.USER_POSITION_HISTORY.LIST,
      label: (
        <MenuLabel
          idLabelMessage="sidebar.positionHistory.entry"
          to={`${url}/${USER_POSITION_HISTORY_PATHS.USER_POSITIONS_HISTORY}`}
        />
      ),
      key: SIDEBAR_MENU_KEYS.POSITION_HISTORY,
      icon: <MenuIcon iconName="history" />,
    }),
    getItem({
      allowedRoles: ALLOWED_ROLES.ORGANIZATIONS.EMERGENCIES.LIST,
      label: (
        <MenuLabel
          idLabelMessage="sidebar.menu.emergencyHistory"
          to={`${url}/${EMERGENCY_PATHS.HISTORY}`}
        />
      ),
      key: SIDEBAR_MENU_KEYS.EMERGENCY_HISTORY,
      icon: <MenuIcon iconName="history" />,
      accessByRemoteConfig: emergenciesAllowed,
    }),
    getItem({
      allowedRoles: ALLOWED_ROLES.ORGANIZATIONS.DOCUMENT_REQUESTS.LIST,
      label: (
        <MenuLabel
          idLabelMessage="sidebar.documents.entry"
          to={`${url}/${DOCUMENT_REQUESTS_PATHS.BASE_URL}`}
        />
      ),
      key: SIDEBAR_MENU_KEYS.DOCUMENT_REQUESTS,
      icon: <MenuIcon iconName="description" />,
      accessByRemoteConfig: timeTrackerUser,
    }),
    timeTracker,
    ownTimeTracker,
    getItem({
      label: (
        <MenuLabel
          idLabelMessage="sidebar.signing.documents"
          to="https://documentos.operonda.com/"
          target="_blank"
        />
      ),
      key: SIDEBAR_MENU_KEYS.SIGNATURE_DOCUMENTS,
      icon: <MenuIcon iconName="signature" />,
      accessByRemoteConfig: timeTrackerUser && isSupervisor,
    }),
  ].filter(item => !isTimeTrackerGated(item));

  const planningItems = [
    getItem({
      allowedRoles: ALLOWED_ROLES.ORGANIZATIONS.DIVISIONS.SCHEDULES.LIST,
      label: (
        <MenuLabel
          idLabelMessage="sidebar.menu.schedules"
          to={`${url}/${SCHEDULES_PATHS.BASE_URL}`}
        />
      ),
      key: SIDEBAR_MENU_KEYS.SCHEDULES,
      icon: <MenuIcon iconName="calendar_today" />,
    }),
    getItem({
      allowedRoles: ALLOWED_ROLES.ORGANIZATIONS.DIVISIONS.SCHEDULES.LIST,
      label: (
        <MenuLabel
          idLabelMessage="sidebar.menu.checkpoint.routes"
          to={`${url}/${CHECKPOINT_ROUTES_PATHS.BASE_URL}`}
        />
      ),
      key: SIDEBAR_MENU_KEYS.CHECKPOINT_ROUTES_SCHEDULED,
      icon: <MenuIcon iconName="route" />,
    }),
    getItem({
      allowedRoles: ALLOWED_ROLES.ORGANIZATIONS.DIVISIONS.WORK_TEMPLATES.LIST,
      label: (
        <MenuLabel
          idLabelMessage="sidebar.menu.work.templates"
          to={`${url}/${WORK_TEMPLATES_PATHS.BASE_URL}`}
        />
      ),
      key: SIDEBAR_MENU_KEYS.WORK_TEMPLATES,
      icon: <MenuIcon iconName="task_alt" />,
    }),
    getItem({
      allowedRoles: ALLOWED_ROLES.ORGANIZATIONS.WORK_JOURNEY.LIST,
      label: (
        <MenuLabel
          idLabelMessage="sidebar.menu.work.journey"
          to={`${url}/${WORK_JOURNEY_PATHS.BASE_URL}`}
        />
      ),
      key: SIDEBAR_MENU_KEYS.WORK_JOURNEY,
      icon: <MenuIcon iconName="timer" />,
    }),
    getItem({
      allowedRoles: ALLOWED_ROLES.ORGANIZATIONS.WORK_SHIFTS.LIST,
      label: (
        <MenuLabel
          idLabelMessage="sidebar.configuration.shifts"
          to={`${url}/${SHIFTS_PATHS.BASE_URL}`}
        />
      ),
      key: SIDEBAR_MENU_KEYS.WORK_SHIFTS,
      icon: <MenuIcon iconName="calendar_month" />,
    }),
    getItem({
      allowedRoles: ALLOWED_ROLES.ORGANIZATIONS.DIVISIONS.TRIGGERS.LIST,
      label: (
        <MenuLabel
          idLabelMessage="sidebar.menu.triggers"
          to={`${url}/${TRIGGERS_PATHS.BASE_URL}`}
        />
      ),
      key: SIDEBAR_MENU_KEYS.TRIGGERS,
      icon: <MenuIcon iconName="flash_on" />,
    }),
    getItem({
      allowedRoles: union(
        ALLOWED_ROLES.ORGANIZATIONS.EVENT_TYPES.LIST,
        ALLOWED_ROLES.ORGANIZATIONS.DIVISIONS.EVENT_TYPES.LIST,
      ),
      label: (
        <MenuLabel
          idLabelMessage="sidebar.menu.forms"
          to={`${url}/${EVENT_TYPES_PATHS.BASE_URL}`}
        />
      ),
      key: SIDEBAR_MENU_KEYS.FORMS,
      icon: <MenuIcon iconName="article" />,
    }),
    getItem({
      allowedRoles: union(
        ALLOWED_ROLES.ORGANIZATIONS.LOCATIONS.LIST,
        ALLOWED_ROLES.ORGANIZATIONS.DIVISIONS.LOCATIONS.LIST,
      ),
      label: (
        <MenuLabel
          idLabelMessage="sidebar.configuration.locations"
          to={`${url}/${LOCATIONS_PATHS.BASE_URL}`}
        />
      ),
      key: SIDEBAR_MENU_KEYS.LOCATIONS,
      icon: <MenuIcon iconName="location_on" />,
    }),
  ].filter(item => !isTimeTrackerGated(item));

  const configurationItems = [
    getItem({
      allowedRoles: ALLOWED_ROLES.ORGANIZATIONS.UPDATE,
      label: (
        <MenuLabel
          idLabelMessage="sidebar.configuration.organization"
          to={`${url}/${CONFIG_PATHS.ORGANIZATION}`}
        />
      ),
      key: SIDEBAR_MENU_KEYS.ORGANIZATION,
      icon: <MenuIcon iconName="business" />,
    }),
    getItem({
      allowedRoles: ALLOWED_ROLES.ORGANIZATIONS.DIVISIONS.GET,
      label: (
        <MenuLabel
          idLabelMessage="sidebar.configuration.divisions"
          to={`${url}/${CONFIG_PATHS.DIVISIONS}`}
        />
      ),
      key: SIDEBAR_MENU_KEYS.DIVISIONS,
      icon: <MenuIcon iconName="account_tree" />,
    }),
    getItem({
      allowedRoles: ALLOWED_ROLES.ORGANIZATIONS.USERS.LIST,
      label: (
        <MenuLabel
          idLabelMessage="sidebar.configuration.users"
          to={`${url}/${getRouteToUsersHome()}`}
        />
      ),
      key: SIDEBAR_MENU_KEYS.USERS,
      icon: <MenuIcon iconName="group" />,
    }),
    getItem({
      allowedRoles: ALLOWED_ROLES.ORGANIZATIONS.DIVISIONS.GROUPS.LIST,
      label: (
        <MenuLabel
          idLabelMessage="sidebar.menu.userGroups"
          to={`${url}/${GROUPS_PATHS.BASE_URL}`}
        />
      ),
      key: SIDEBAR_MENU_KEYS.GROUPS,
      icon: <MenuIcon iconName="groups" />,
    }),
    getItem({
      allowedRoles: ALLOWED_ROLES.ORGANIZATIONS.JOBS.LIST,
      label: (
        <MenuLabel
          idLabelMessage="sidebar.configuration.job"
          to={`${url}/${JOB_PATHS.BASE_URL}`}
        />
      ),
      key: SIDEBAR_MENU_KEYS.JOBS,
      icon: <MenuIcon iconName="work" />,
    }),
    getItem({
      allowedRoles: ALLOWED_ROLES.ORGANIZATIONS.OPTION_LISTS.LIST,
      label: (
        <MenuLabel
          idLabelMessage="sidebar.configuration.optionLists"
          to={`${url}/${OPTION_LIST_PATHS.BASE_URL}`}
        />
      ),
      key: SIDEBAR_MENU_KEYS.OPTION_LISTS,
      icon: <MenuIcon iconName="list" />,
    }),
    getItem({
      allowedRoles: ALLOWED_ROLES.ORGANIZATIONS.COST_CENTERS.LIST,
      label: (
        <MenuLabel
          idLabelMessage="sidebar.configuration.costCenters"
          to={`${url}/${COST_CENTERS_PATHS.BASE_URL}`}
        />
      ),
      key: SIDEBAR_MENU_KEYS.COST_CENTERS,
      icon: <MenuIcon iconName="account_balance" />,
      accessByRemoteConfig: costCentersAllowed,
    }),
    getItem({
      allowedRoles: ALLOWED_ROLES.ORGANIZATIONS.HOLIDAYS.LIST,
      label: (
        <MenuLabel
          idLabelMessage="sidebar.configuration.holidays"
          to={`${url}/${HOLIDAYS_PATHS.BASE_URL}`}
        />
      ),
      key: SIDEBAR_MENU_KEYS.HOLIDAYS,
      icon: <MenuIcon iconName="event" />,
      accessByRemoteConfig: timeTrackerUser,
    }),
    getItem({
      allowedRoles: ALLOWED_ROLES.ORGANIZATIONS.ADDRESSES.LIST,
      label: (
        <MenuLabel
          idLabelMessage="sidebar.configuration.addresses"
          to={`${url}/${ADDRESSES_PATHS.BASE_URL}`}
        />
      ),
      key: SIDEBAR_MENU_KEYS.ADDRESSES,
      icon: <MenuIcon iconName="home_work" />,
      accessByRemoteConfig: timeTrackerUser,
    }),
    getItem({
      allowedRoles: ALLOWED_ROLES.ORGANIZATIONS.GET,
      label: (
        <MenuLabel
          idLabelMessage="sidebar.configuration.operondaApi"
          to={`${url}/${OPERONDA_API_PATHS.BASE_URL}`}
        />
      ),
      key: SIDEBAR_MENU_KEYS.OPERONDA_API,
      icon: <MenuIcon iconName="api" />,
    }),
    getItem({
      allowedRoles: ALLOWED_ROLES.ORGANIZATIONS.EMERGENCY_TYPES.LIST,
      label: (
        <MenuLabel
          idLabelMessage="sidebar.menu.emergencyTypes"
          to={`${url}/${EMERGENCY_PATHS.TYPES}`}
        />
      ),
      key: SIDEBAR_MENU_KEYS.EMERGENCY_TYPES,
      icon: <MenuIcon iconName="category" />,
      accessByRemoteConfig: emergenciesAllowed,
    }),
    getItem({
      allowedRoles: ALLOWED_ROLES.ORGANIZATIONS.EMERGENCY_INTEGRATIONS.LIST,
      label: (
        <MenuLabel
          idLabelMessage="sidebar.menu.emergencyIntegrations"
          to={`${url}/${EMERGENCY_PATHS.INTEGRATIONS}`}
        />
      ),
      key: SIDEBAR_MENU_KEYS.EMERGENCY_INTEGRATIONS,
      icon: <MenuIcon iconName="hub" />,
      accessByRemoteConfig: emergenciesAllowed,
    }),
    getItem({
      allowedRoles: ALLOWED_ROLES.ORGANIZATIONS.AUDIT_LOGS.LIST,
      label: (
        <MenuLabel
          idLabelMessage="sidebar.configuration.auditLogs"
          to={`${url}/${CONFIG_PATHS.AUDIT_LOGS}`}
        />
      ),
      key: SIDEBAR_MENU_KEYS.AUDIT_LOGS,
      icon: <MenuIcon iconName="policy" />,
    }),
  ];

  const realtimeControlSection = createNavSection(
    SIDEBAR_MENU_KEYS.REALTIME_CONTROL,
    realtimeControlItems,
  );
  const operationsSection = createNavSection(SIDEBAR_MENU_KEYS.OPERATIONS, operationsItems);
  const planningSection = createNavSection(SIDEBAR_MENU_KEYS.PLANNING, planningItems);
  const configurationSection = createNavSection(
    SIDEBAR_MENU_KEYS.CONFIGURATION,
    configurationItems,
  );

  return compactMenuItems([
    homeItem,
    realtimeControlSection,
    operationsSection,
    planningSection,
    configurationSection,
  ]);
};
